import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { X } from "lucide-react";
import { decryptToken } from "../utils/getToken";

const ScheduleInterview = ({ candidate = {}, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    datetime: "",
    message: "",

  });
  const token = decryptToken("accessToken") || ""
  const [isAlreadyScheduled, setIsAlreadyScheduled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const apiUrl = process.env.REACT_APP_LINK;

  useEffect(() => {
    const checkSchedule = async () => {
      try {
        const res = await fetch(`${apiUrl}/schedule`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`
          }
        });
        const data = await res.json();

        const alreadyExists = data.some(
          (s) => s.CandidateEmail === candidate.CandidateEmail
        );
        setIsAlreadyScheduled(alreadyExists);
      } catch (err) {
        console.error("Failed to check schedule:", err);
      }
    };

    checkSchedule();
  }, [candidate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();

  //   if (isAlreadyScheduled) {
  //     toast.warning("Interview already scheduled.");
  //     return;
  //   }

  //   const finalData = {
  //     CandidateName: candidate.CandidateName || "Candidate",
  //     CandidatePhone: candidate.CandidatePhone || "",
  //     CandidateEmail: candidate.CandidateEmail || "",
  //     DateTime: formData.datetime,
  //     Message: formData.message,
  //   };

  //   try {
  //     setIsLoading(true);

  //     const res = await fetch(`${apiUrl}/schedule`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify(finalData),
  //     });

  //     if (res.ok) {
  //       toast.success("Interview scheduled. WhatsApp and email sent.");
  //       if (onSubmit) onSubmit(finalData);

  //       setTimeout(() => {
  //         onClose();
  //       }, 2000);
  //     } else {
  //       toast.error("Failed to schedule interview.");
  //     }
  //   } catch (err) {
  //     console.error("Error scheduling interview:", err);
  //     toast.error("Something went wrong.");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalData = {
      CandidateName: candidate.CandidateName || "Candidate",
      CandidatePhone: candidate.CandidatePhone || "",
      CandidateEmail: candidate.CandidateEmail || "",
      DateTime: formData.datetime,
      Message: formData.message,
    };

    try {
      setIsLoading(true);

      let url = `${apiUrl}/schedule`;
      let method = "POST";

      if (isAlreadyScheduled) {
        // If candidate already has schedule → update
        url = `${apiUrl}/schedule/${candidate.CandidateEmail}`;
        method = "PUT"; // depends on your backend
      }

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(finalData),
      });

      if (res.ok) {
        toast.success(
          isAlreadyScheduled
            ? "Interview rescheduled successfully!"
            : "Interview scheduled. WhatsApp and email sent."
        );
        if (onSubmit) onSubmit(finalData);

        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        toast.error("Failed to schedule interview.");
      }
    } catch (err) {
      console.error("Error scheduling interview:", err);
      toast.error("Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-[99999] flex flex-col justify-end items-center">
      <div className="bg-white w-full max-h-[90vh] max-w-[600px] rounded-t-2xl shadow-xl p-5 overflow-y-auto transition-transform duration-300 animate-slide-up">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800">Schedule Interview</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-500 text-sm font-medium"
          >
            <X />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3 text-sm">
          {/* Candidate Name */}
          <div>
            <label className="block text-gray-600 mb-1">Candidate Name</label>
            <input
              type="text"
              value={candidate.CandidateName || ""}
              disabled
              className="w-full border rounded-lg px-3 py-2 text-gray-700 bg-gray-100 text-sm"
            />
          </div>

          {/* Candidate Email */}
          <div>
            <label className="block text-gray-600 mb-1">Candidate Email</label>
            <input
              type="email"
              value={candidate.CandidateEmail || ""}
              disabled
              className="w-full border rounded-lg px-3 py-2 text-gray-700 bg-gray-100 text-sm"
            />
          </div>

          {/* Candidate Phone */}
          <div>
            <label className="block text-gray-600 mb-1">Candidate Phone</label>
            <input
              type="text"
              value={candidate.CandidatePhone || ""}
              disabled
              className="w-full border rounded-lg px-3 py-2 text-gray-700 bg-gray-100 text-sm"
            />
          </div>

          {/* Date & Time */}
          <div className="w-full">
            <label className="block text-gray-600 mb-1 font-medium">
              Date & Time
            </label>

            <DatePicker
              selected={formData.datetime ? new Date(formData.datetime) : null}
              onChange={(date) =>
                setFormData((prev) => ({ ...prev, datetime: date }))
              }
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              dateFormat="MMMM d, yyyy h:mm aa"
              minDate={new Date()}
              placeholderText="Select date and time"
              className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-gray-600 mb-1">Message</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Add additional notes..."
              className="w-full border rounded-lg px-3 py-2 text-gray-700 text-sm min-h-[80px] focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2 mt-5">
            <button
              type="button"
              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:bg-blue-300 disabled:cursor-not-allowed"
            >
              {isLoading
                ? "Saving..."
                : isAlreadyScheduled
                  ? "Reschedule Interview"
                  : "Schedule Interview"}
            </button>

          </div>
        </form>
      </div>
    </div>
  );
};

export default ScheduleInterview;
